import { useMemo, useState, type FormEvent } from "react";
import {
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  PackagePlus,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import type {
  Currency,
  NewProduct,
  Product,
  ProductStatus,
} from "../domain/types";
import { PlatformBadge } from "../components/PlatformBadge";
import {
  Button,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusChip,
} from "../components/UI";
import { extractProductId, money } from "../lib/format";
import { useWeSell } from "../state/WeSellProvider";

interface ProductFormState {
  platformId: string;
  name: string;
  platformProductId: string;
  originalUrl: string;
  affiliateUrl: string;
  imageUrl: string;
  description: string;
  category: string;
  price: string;
  currency: Currency;
  status: ProductStatus;
  published: boolean;
}

function formFrom(
  product: Product | null,
  platformId: string,
): ProductFormState {
  return product
    ? {
        platformId: product.platformId,
        name: product.name,
        platformProductId: product.platformProductId,
        originalUrl: product.originalUrl,
        affiliateUrl: product.affiliateUrl,
        imageUrl: product.imageUrl,
        description: product.description,
        category: product.category,
        price: product.price === null ? "" : String(product.price),
        currency: product.currency,
        status: product.status,
        published: product.published,
      }
    : {
        platformId,
        name: "",
        platformProductId: "",
        originalUrl: "",
        affiliateUrl: "",
        imageUrl: "",
        description: "",
        category: "",
        price: "",
        currency: "BRL",
        status: "active",
        published: true,
      };
}

export function Products() {
  const { data, saving, addProduct, editProduct } = useWeSell();
  const [query, setQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductFormState>(() => formFrom(null, ""));
  const [formError, setFormError] = useState("");
  const [copied, setCopied] = useState("");

  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("pt-BR");
    return (data?.products ?? []).filter((product) => {
      const matchesQuery =
        !normalized ||
        `${product.name} ${product.shortCode} ${product.platformProductId}`
          .toLocaleLowerCase("pt-BR")
          .includes(normalized);
      return (
        matchesQuery &&
        (platformFilter === "all" || product.platformId === platformFilter)
      );
    });
  }, [data?.products, platformFilter, query]);

  if (!data) return null;

  const openNew = () => {
    setEditing(null);
    setForm(formFrom(null, data.platforms[0]?.id ?? ""));
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    setForm(formFrom(product, product.platformId));
    setFormError("");
    setModalOpen(true);
  };

  const change = <K extends keyof ProductFormState>(
    key: K,
    value: ProductFormState[K],
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleOriginalUrl = (value: string) => {
    setForm((current) => ({
      ...current,
      originalUrl: value,
      platformProductId: current.platformProductId || extractProductId(value),
    }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError("");
    if (!form.name.trim() || !form.platformId) {
      setFormError("Nome e plataforma são obrigatórios.");
      return;
    }
    if (form.published && !form.affiliateUrl.trim()) {
      setFormError(
        "Para publicar na vitrine, informe o link de afiliada que registra sua comissão.",
      );
      return;
    }
    const input: NewProduct = {
      platformId: form.platformId,
      name: form.name.trim(),
      platformProductId: form.platformProductId.trim(),
      originalUrl: form.originalUrl.trim(),
      affiliateUrl: form.affiliateUrl.trim(),
      imageUrl: form.imageUrl.trim(),
      description: form.description.trim(),
      category: form.category.trim(),
      price: form.price ? Number(form.price.replace(",", ".")) : null,
      currency: form.currency,
      status: form.status,
      published: form.published,
    };
    try {
      if (editing) await editProduct(editing.id, input);
      else await addProduct(input);
      setModalOpen(false);
    } catch {
      setFormError(
        "Não foi possível salvar. Confira os campos e tente novamente.",
      );
    }
  };

  const copyCode = async (code: string) => {
    await navigator.clipboard.writeText(code);
    setCopied(code);
    window.setTimeout(() => setCopied(""), 1800);
  };

  return (
    <>
      <PageHeader
        eyebrow="Catálogo"
        title="Produtos"
        description="Cada item ganha um código simples para divulgar sem perder o link que gera comissão."
        action={
          <Button onClick={openNew}>
            <PackagePlus size={18} aria-hidden="true" /> Novo produto
          </Button>
        }
      />

      <div className="toolbar">
        <label className="search-field">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Buscar produtos</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar por nome, T001 ou ID do produto"
          />
        </label>
        <label className="select-field">
          <SlidersHorizontal size={18} aria-hidden="true" />
          <span className="sr-only">Filtrar por plataforma</span>
          <select
            value={platformFilter}
            onChange={(event) => setPlatformFilter(event.target.value)}
          >
            <option value="all">Todas as plataformas</option>
            {data.platforms.map((platform) => (
              <option key={platform.id} value={platform.id}>
                {platform.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {filtered.length ? (
        <div className="product-grid">
          {filtered.map((product) => {
            const platform = data.platforms.find(
              (item) => item.id === product.platformId,
            );
            return (
              <article className="product-card" key={product.id}>
                <button
                  className="product-card__image"
                  onClick={() => openEdit(product)}
                  aria-label={`Editar ${product.name}`}
                >
                  {product.imageUrl ? (
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      width="420"
                      height="300"
                      loading="lazy"
                    />
                  ) : (
                    <span>Sem imagem</span>
                  )}
                  <span className="product-card__code">
                    {product.shortCode}
                  </span>
                </button>
                <div className="product-card__body">
                  <div className="product-card__meta">
                    {platform ? (
                      <PlatformBadge platform={platform} compact />
                    ) : null}
                    <StatusChip
                      status={product.published ? "ok" : "neutral"}
                      label={product.published ? "Na vitrine" : "Privado"}
                    />
                  </div>
                  <button
                    className="product-card__title"
                    onClick={() => openEdit(product)}
                  >
                    {product.name}
                  </button>
                  <p>
                    {product.category || "Sem categoria"}
                    {product.price !== null
                      ? ` · ${money(product.price, product.currency)}`
                      : ""}
                  </p>
                  <div className="product-card__metrics">
                    <span>
                      <strong>{product.clicks}</strong> cliques
                    </span>
                    <span>
                      <strong>{product.salesCount}</strong> vendas
                    </span>
                    <span>
                      <strong>{money(product.commissionTotal)}</strong> comissão
                    </span>
                  </div>
                  <div className="product-card__actions">
                    <Button
                      variant="secondary"
                      onClick={() => copyCode(product.shortCode)}
                    >
                      <Copy size={17} aria-hidden="true" />{" "}
                      {copied === product.shortCode
                        ? "Copiado"
                        : product.shortCode}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        void editProduct(product.id, {
                          published: !product.published,
                        })
                      }
                    >
                      {product.published ? (
                        <EyeOff size={17} aria-hidden="true" />
                      ) : (
                        <Eye size={17} aria-hidden="true" />
                      )}
                      {product.published ? "Ocultar" : "Publicar"}
                    </Button>
                    {product.affiliateUrl ? (
                      <a
                        className="icon-button"
                        href={product.affiliateUrl}
                        target="_blank"
                        rel="noreferrer sponsored"
                        aria-label={`Abrir link de ${product.name}`}
                      >
                        <ExternalLink size={18} aria-hidden="true" />
                      </a>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={<PackagePlus size={28} />}
          title="Nenhum produto encontrado"
          description="Cadastre o primeiro item comprado e ele receberá um código curto automaticamente."
          action={<Button onClick={openNew}>Cadastrar produto</Button>}
        />
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Editar ${editing.shortCode}` : "Novo produto"}
        description="O ID identifica o item; o link de afiliada é o que registra a comissão."
        footer={
          <>
            <Button
              variant="secondary"
              type="button"
              onClick={() => setModalOpen(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" form="product-form" loading={saving}>
              Salvar produto
            </Button>
          </>
        }
      >
        <form id="product-form" className="form-grid" onSubmit={submit}>
          <Field label="Plataforma">
            <select
              value={form.platformId}
              onChange={(event) => change("platformId", event.target.value)}
              required
            >
              {data.platforms.map((platform) => (
                <option key={platform.id} value={platform.id}>
                  {platform.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Nome do produto">
            <input
              value={form.name}
              onChange={(event) => change("name", event.target.value)}
              required
            />
          </Field>
          <Field
            label="Link original"
            helper="Ao colar um link da Amazon ou Temu, tentamos extrair o ID automaticamente."
          >
            <input
              type="url"
              value={form.originalUrl}
              onChange={(event) => handleOriginalUrl(event.target.value)}
            />
          </Field>
          <Field label="ID na plataforma">
            <input
              value={form.platformProductId}
              onChange={(event) =>
                change("platformProductId", event.target.value)
              }
            />
          </Field>
          <Field
            label="Link de afiliada"
            helper="Obrigatório para aparecer na vitrine."
          >
            <input
              type="url"
              value={form.affiliateUrl}
              onChange={(event) => change("affiliateUrl", event.target.value)}
            />
          </Field>
          <Field label="Imagem">
            <input
              type="url"
              value={form.imageUrl}
              onChange={(event) => change("imageUrl", event.target.value)}
            />
          </Field>
          <Field label="Categoria">
            <input
              value={form.category}
              onChange={(event) => change("category", event.target.value)}
            />
          </Field>
          <div className="form-row">
            <Field label="Preço">
              <input
                inputMode="decimal"
                value={form.price}
                onChange={(event) => change("price", event.target.value)}
              />
            </Field>
            <Field label="Moeda">
              <select
                value={form.currency}
                onChange={(event) =>
                  change("currency", event.target.value as Currency)
                }
              >
                <option>BRL</option>
                <option>CLP</option>
                <option>USD</option>
                <option>EUR</option>
              </select>
            </Field>
          </div>
          <Field label="Descrição">
            <textarea
              rows={3}
              value={form.description}
              onChange={(event) => change("description", event.target.value)}
            />
          </Field>
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(event) => change("published", event.target.checked)}
            />
            <span>
              <strong>Mostrar na vitrine pública</strong>
              <small>
                As pessoas poderão abrir o link de afiliada usando o código
                curto.
              </small>
            </span>
          </label>
          {formError ? (
            <p className="form-error" role="alert">
              {formError}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
